import React, { useEffect, useState } from 'react'
import { Link, useOutletContext } from 'react-router-dom'
import { useSocketState } from '../../helpers/useSocket'
import { TRACKED_PAGES, normalizePagePath } from '../../helpers/pageInstances'
import { isAuthEnabled, getToken } from '../../helpers/auth'

function formatDuration(ms) {
	const totalSeconds = Math.max(0, Math.floor(ms / 1000))
	const hours = Math.floor(totalSeconds / 3600)
	const minutes = Math.floor((totalSeconds % 3600) / 60)
	const seconds = totalSeconds % 60
	if (hours > 0) return `${hours}h ${minutes}m`
	if (minutes > 0) return `${minutes}m ${seconds}s`
	return `${seconds}s`
}

function formatTime(timestamp) {
	return timestamp ? new Date(timestamp).toLocaleTimeString() : '-'
}

// vMix's embedded browser doesn't identify itself, it reports this exact (outdated) Chrome user agent
const VMIX_USER_AGENT = 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/115.0.0.0 Safari/537.36'

function describeClient(userAgent) {
	if (/OBS\//.test(userAgent)) return 'OBS'
	if (userAgent === VMIX_USER_AGENT || /vMix/i.test(userAgent)) return 'vMix'
	if (/Edg\//.test(userAgent)) return 'Edge'
	if (/Firefox\//.test(userAgent)) return 'Firefox'
	if (/Chrome\//.test(userAgent)) return 'Chrome'
	if (/Safari\//.test(userAgent)) return 'Safari'
	return 'Browser'
}

// Same page and query as the instance, with the dashboard's token so it passes auth
function openUrl(instance) {
	const params = new URLSearchParams(instance.search)
	if (isAuthEnabled() && getToken()) params.set('token', getToken())
	const query = params.toString()
	return `${instance.page}${query ? `?${query}` : ''}`
}

export default function PagesDashboard() {
	const { socket } = useOutletContext()
	const [instances] = useSocketState('PageInstances')
	const [now, setNow] = useState(Date.now())

	useEffect(() => {
		const interval = setInterval(() => setNow(Date.now()), 1000)
		return () => clearInterval(interval)
	}, [])

	const list = instances ?? []
	const connectedCount = list.filter((i) => i.connected).length
	const hasDisconnected = connectedCount < list.length

	// Group instances per page, in the order of TRACKED_PAGES
	const groups = Object.entries(TRACKED_PAGES)
		.map(([path, label]) => ({ path, label, instances: list.filter((i) => normalizePagePath(i.page) === path) }))
		.filter((group) => group.instances.length > 0)

	function reloadAll() {
		if (window.confirm(`Refresh all ${connectedCount} connected pages?`)) {
			socket.emit('reloadAllPageInstances')
		}
	}

	return (
		<div className="mt-4 flex flex-col gap-2">
			<div className="flex flex-row gap-2">
				<button disabled={connectedCount === 0} onClick={reloadAll}>
					Refresh All
				</button>
				<button disabled={!hasDisconnected} onClick={() => socket.emit('clearDisconnectedPageInstances')}>
					Clear Disconnected
				</button>
			</div>

			<table className="text-left">
				<thead>
					<tr>
						<th className="pr-6">Websocket</th>
						<th className="pr-6">Connected At</th>
						<th className="pr-6">Opened At</th>
						<th className="pr-6">Client</th>
						<th className="pr-6">Query</th>
						<th></th>
					</tr>
				</thead>
				<tbody>
					{list.length === 0 && (
						<tr>
							<td colSpan={6}>No pages open</td>
						</tr>
					)}
					{groups.map((group) => (
						<React.Fragment key={group.path}>
							<tr>
								<td colSpan={6} className="pt-4 font-bold">
									{group.label} ({group.instances.length})
								</td>
							</tr>
							{group.instances.map((instance) => (
								<tr key={instance.instanceId}>
									<td className="pr-6">
										<span className={`inline-block w-3 h-3 mr-2 rounded-full ${instance.connected ? 'bg-green-500' : 'bg-red-500'}`} />
										{instance.connected ? 'Connected' : 'Disconnected'}
									</td>
									<td className="pr-6">
										{instance.connected
											? `${formatTime(instance.connectedAt)} (${formatDuration(now - instance.connectedAt)})`
											: `Lost at ${formatTime(instance.disconnectedAt)}`}
									</td>
									<td className="pr-6">{formatTime(instance.openedAt)}</td>
									<td className="pr-6" title={instance.userAgent}>
										{describeClient(instance.userAgent)}
									</td>
									<td className="pr-6">{instance.search || '-'}</td>
									<td className="py-1">
										<button disabled={!instance.connected} onClick={() => socket.emit('reloadPageInstance', instance.instanceId)}>
											Refresh
										</button>
										<Link className="ml-2" to={openUrl(instance)} target="_blank">
											<button>Open</button>
										</Link>
									</td>
								</tr>
							))}
						</React.Fragment>
					))}
				</tbody>
			</table>
		</div>
	)
}
